import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { WorkflowIntelligenceProvider } from "./contexts/WorkflowIntelligenceContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { registerActions } from "./actions";
import { ribbonService } from "./services/ribbon.service";
import { initAppInsights } from "./services/app-insights.service";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { isStandaloneMode } from "./utils/standalone-mode";

// Start telemetry as early as possible so unhandled errors during Office init
// or React mount are captured. No-ops in local dev without an injected key.
initAppInsights();

/* global document, Office, module, require, HTMLElement */

const title = "Workflow Intelligence";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

const renderApp = () => {
  root?.render(
    <FluentProvider theme={webLightTheme}>
      {/* ErrorBoundary wraps everything below FluentProvider so the fallback
          UI inherits the theme. Anything above (FluentProvider itself, the
          createRoot call, Office.onReady) is the SDK's window.onerror beat. */}
      <ErrorBoundary>
        <WorkflowIntelligenceProvider>
          <FeatureFlagProvider>
            <App title={title} />
          </FeatureFlagProvider>
        </WorkflowIntelligenceProvider>
      </ErrorBoundary>
    </FluentProvider>
  );
};

if (isStandaloneMode()) {
  // Standalone: no Excel host, no Office.onReady, no ribbon service, no
  // Office.actions.associate. Render immediately against the URL-provided
  // spreadsheetId. `office.js` is still loaded from index.html but its
  // callbacks are never depended on in this path.
  renderApp();
} else {
  let count = 0;

  // Sanity check that shared runtime is set up properly.
  Office.onReady(() => {
    Office.addin.onVisibilityModeChanged((args) => {
      if (args.visibilityMode === Office.VisibilityMode.taskpane) {
        count++;
        console.log("Task pane opened " + count + " times.");
      }
    });

    // Initialize the ribbon service for worksheet status indicators
    ribbonService.initialize().catch((error) => {
      console.error("Failed to initialize ribbon service:", error);
    });
  });

  // Register all Office actions (ribbon buttons and keyboard shortcuts)
  registerActions();

  // Render application after Office initializes
  Office.onReady(() => {
    renderApp();
  });
}

if ((module as any).hot) {
  (module as any).hot.accept("./App", () => {
    const NextApp = require("./App").default;
    root?.render(NextApp);
  });
}
